import { MeetingRecipient } from './../../interfaces/meetings/meeting-recipients.interface';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Component, inject, input, OnInit, signal } from '@angular/core';
import { Action, ActionDemander, ActionRecipient, ActionTarget } from '@shared/interfaces/actions';
import { MeetingAction } from '@shared/interfaces/meetings';
import { AddRecipientsToPoint, PointAction, PointRecipient } from '@shared/interfaces/points';
import { PointsService } from '@shared/services/points.service';
import { TogglerConfig } from '@shared/types/dev';
import { catchError, distinctUntilChanged, finalize, Observable, of, skip } from 'rxjs';
import { UserInfo } from '@shared/models/user-info';
import { iAdminService } from 'src/app/core/services/iAdmin.service';
import { ITabs } from '@devinforius/i-navigation';
import { DialogService } from 'primeng/dynamicdialog';
import { AddRecipientsComponent } from '../add-recipients/add-recipients.component';
import { MeetingsService } from '@shared/services/meetings.service';
import { NotificationSuccess } from 'src/app/core/models/notifcation-success';
import { Notifier } from 'src/app/core/services';
import { ITableColumn, ITableItem } from '@devinforius/super-compos-17';
import { DatePipe } from '@angular/common';
import { FormOptions } from 'src/app/common/models/form-options';
import { toCapitalCase } from '@shared/helpers';
import { BaseTableConfig } from '@shared/models/dev';
import { MenuItem } from 'primeng/api';
import { NotificationError, NotificationWarn } from 'src/app/core/models';
import { EmptyResultPlaceholderOptions } from '@shared/interfaces/dev';
import { IContactService } from '@core/services/i-contact.service';
import { Contact } from '@shared/models/iContact/contact';
import { ITableCategoryAction } from '@devinforius/super-compos-17/lib/i-table/models/i-table-category-action.model';

@Component({
  selector: 'idel-recipients-n-actions',
  templateUrl: './recipients-n-actions.component.html',
  styleUrl: './recipients-n-actions.component.scss',
})
export class RecipientsNActionsComponent implements OnInit {
  private readonly pointsService = inject(PointsService);
  private readonly meetingsService = inject(MeetingsService);
  private readonly dialogService = inject(DialogService);
  private readonly iContactsService = inject(IContactService);

  private readonly formOptions = inject(FormOptions);
  private readonly notifier = inject(Notifier);
  private readonly datePipe = inject(DatePipe);

  public readonly target = input.required<ActionTarget>();

  public readonly userInfos = toSignal<UserInfo>(inject(iAdminService).getUserInfo());
  public readonly actions = signal<PointAction[] | MeetingAction[]>([]);
  public readonly recipients = signal<PointRecipient[] | MeetingRecipient[]>([]);
  public readonly fetchingActionsAndRecipients = signal<boolean>(false);
  public readonly isAddActionFormDialogOpen = signal<boolean>(false);
  public readonly isAddRecipientFormDialogOpen = signal<boolean>(false);
  public readonly isActionProcessDialogOpen = signal<boolean>(false);
  public readonly currentLayout = signal<'simplified' | 'table'>('simplified');
  public readonly currentTab = signal<'actions' | 'recipients'>('actions');
  public readonly actionToProcess = signal<PointAction | MeetingAction | null>(null);
  public readonly actionsColumns = signal<ITableColumn[]>([]);
  public readonly recipientsColumns = signal<ITableColumn[]>([]);
  public readonly contacts = signal<Contact[]>([]);

  public readonly actionsTableConfig = new BaseTableConfig();
  public readonly recipientsTableConfig = new BaseTableConfig().withCheckableRows({ delete: true });
  public readonly emptyResultOptions: EmptyResultPlaceholderOptions = {
    showPageRefreshPossibility: true,
    retry: () => this.refreshRecipients(),
  };

  public actionsTableActions: MenuItem[] = [
    {
      label: 'Traiter',
      icon: 'bi bi-lightning-charge-fill',
      command: (event) => {
        console.log(event);
      },
    },
  ];
  public recipientsTableActions = signal<MenuItem[]>([
    {
      label: 'Définir comme principal',
      icon: 'bi bi-star-fill',
      command: (selectedRecipients) => {
        const recipients = selectedRecipients as { main: boolean; kind: 'user' | 'service' | 'group'; id: number }[];

        if (recipients.length !== 1 || recipients[0].kind !== 'user' || recipients[0].main) {
          this.notifier.showWarning(
            new NotificationWarn({
              message:
                "Veuillez sélectionner un SEUL destinataire qui est un UTILISATEUR et qui n'est pas déjà principal.",
            })
          );
          return;
        }

        this.setRecipientAsMain(recipients[0].id);
      },
    },
  ]);

  public readonly tabs: ITabs[] = [
    {
      header: 'Pour action',
      id: 'actions',

      active: true,
    },
    {
      header: 'Pour information',
      id: 'recipients',
    },
  ];
  public readonly toggleConfig: TogglerConfig<'simplified', 'table'> = [
    {
      label: 'Vue simplifiée',
      value: 'bi bi-view-list',
      tooltip: 'Vue simplifiée',
      click: () => this.currentLayout.set('simplified'),
    },
    {
      label: 'Vue tableau',
      value: 'bi bi-justify',
      tooltip: 'Vue tableau',
      click: () => this.currentLayout.set('table'),
    },
  ];
  public readonly actionsMenu: ITableCategoryAction[] = [
    {
      label: 'Options',
      actions: [
        {
          icon: 'bi bi-lightning-charge-fill',
          label: "Traiter l'action",
          action: (item: Action) => {
            if (!item.processable) {
              this.notifier.showError(new NotificationError({ message: 'Cette action ne peut être traitée' }));
              return;
            }

            this.onActionProcessClick(item);
          },
          condition: true,
        },
      ],
    },
  ];

  constructor() {
    // Re-fetch when navigating between points/meetings while this tab is active
    toObservable(this.target)
      .pipe(
        distinctUntilChanged((a, b) => a.id === b.id && a.scope === b.scope),
        skip(1),
        takeUntilDestroyed()
      )
      .subscribe(() => this.fetchActionsAndRecipients());
  }

  ngOnInit(): void {
    this.initOptions();
  }

  public onTabChange(tabId: 'actions' | 'recipients'): void {
    this.currentTab.set(tabId);
  }

  private initOptions(): void {
    this.iContactsService
      .getLowContactsAdmin()
      .pipe(catchError(() => of([] as Contact[])))
      .subscribe({
        next: (contacts) => {
          this.contacts.set(contacts);
          this.fetchActionsAndRecipients();
        },
      });
  }

  private fetchActionsAndRecipients(): void {
    const { id, scope } = this.target();

    this.fetchingActionsAndRecipients.set(true);

    if (scope === 'point') {
      this.pointsService
        .fetchPointsActionsAndRecipients(id)
        .pipe(finalize(() => this.fetchingActionsAndRecipients.set(false)))
        .subscribe({
          next: ({ actions, recipients }) => {
            this.actions.set(actions);
            this.recipients.set(recipients);

            this.setTablesColumns();
          },
        });
    } else {
      this.meetingsService
        .fetchMeetingActionsAndRecipients(id)
        .pipe(finalize(() => this.fetchingActionsAndRecipients.set(false)))
        .subscribe({
          next: ({ actions, recipients }) => {
            this.actions.set(actions);
            this.recipients.set(recipients);

            this.setTablesColumns();
          },
        });
    }
  }

  private setTablesColumns(): void {
    const actionsColumns: ITableColumn[] = [
      {
        field: 'createdAt',
        header: 'Date de transmission',
        format: (value: Date) => toCapitalCase(this.datePipe.transform(value, 'EEE dd/MM/yyyy')) ?? '',
      },
      {
        field: 'deadline',
        header: 'Echéance',
        format: (value: Date | null) => toCapitalCase(this.datePipe.transform(value, 'EEE dd/MM/yyyy')) ?? '',
      },
      {
        field: 'demander',
        header: 'Demandeur',
        format: (value: ActionDemander) => {
          const foundUser = this.formOptions.users().find((user) => user.userId === value.id);
          return foundUser ? `${foundUser.nom.toUpperCase()} ${foundUser.prenom}` : 'Inconnu';
        },
      },
      {
        field: 'recipient',
        header: 'Destinataire',
        format: (value: ActionRecipient) => {
          switch (value.kind) {
            case 'user':
              const foundUserContact = this.contacts().find((contact) => contact.id === value.id);

              return foundUserContact
                ? `${foundUserContact.nom.toUpperCase()} ${foundUserContact.prenom}`
                : 'Utilisateur inconnu';
            case 'service':
              const foundService = this.formOptions.services().find((service) => service.id === value.id);
              return foundService ? `Service - ${foundService.description}` : 'Service inconnu';
            case 'group':
              const foundGroup = this.formOptions.groups().find((group) => group.id === value.id);
              return foundGroup ? `Groupe - ${foundGroup.description}` : 'Groupe inconnu';
            default:
              return 'Inconnu';
          }
        },
      },
      {
        field: 'subject',
        header: 'Action',
        format: (_, item: Action) => {
          return item.purpose ?? '';
        },
      },
      {
        field: 'message',
        header: 'Message',
      },
      {
        field: 'answer',
        header: 'Réponse',
      },
    ];

    const recipientsColumns: ITableColumn[] = [
      /**
       * Has to be set in order te be able to select rows in the recipients table, as the row identity is based on the id of the recipient (which is not the case for actions, as they are not selectable)
       */
      {
        field: 'detailId',
        header: 'id',
        visible: false,
        format: (_, item: PointRecipient | MeetingRecipient) => {
          return item.recipient.detailId;
        },
      },
      /**
       * Has to be set in order te be able to select rows in the recipients table, as the row identity is based on the id of the recipient (which is not the case for actions, as they are not selectable)
       */
      {
        field: 'kind',
        header: 'kind',
        visible: false,
        format: (_, item: PointRecipient | MeetingRecipient) => {
          return item.recipient.kind;
        },
      },
      /**
       * Has to be set in order te be able to select rows in the recipients table, as the row identity is based on the id of the recipient (which is not the case for actions, as they are not selectable)
       */
      {
        field: 'main',
        header: 'mainRecipient',
        visible: false,
        format: (_, item: PointRecipient | MeetingRecipient) => {
          return item.mainRecipient;
        },
      },
      {
        field: 'mainRecipient',
        header: 'Principal',
        format: (value: boolean, item: PointRecipient | MeetingRecipient) => {
          if (item.recipient.kind !== 'user') return '';

          return `<span class="main"><i class="bi ${value ? 'bi-star-fill' : 'bi-star'}"></i></span>`;
        },
      },
      {
        field: 'createdAt',
        header: 'Envoyé le',
        format: (value: Date) => toCapitalCase(this.datePipe.transform(value, 'EEE dd/MM/yyyy')) ?? '',
      },
      {
        field: 'demander',
        header: 'De',
        format: (value: ActionDemander) => {
          const foundUser = this.formOptions.users().find((user) => user.userId === value.id);
          return foundUser ? `${foundUser.nom.toUpperCase()} ${foundUser.prenom}` : 'Inconnu';
        },
      },
      {
        field: 'recipient',
        header: 'À',
        format: (value: ActionRecipient) => {
          switch (value.kind) {
            case 'user':
              const foundUserContact = this.contacts().find((contact) => contact.id === value.id);

              return foundUserContact
                ? `${foundUserContact.nom.toUpperCase()} ${foundUserContact.prenom}`
                : 'Utilisateur inconnu';
            case 'service':
              const foundService = this.formOptions.services().find((service) => service.id === value.id);
              return foundService ? `Service - ${foundService.description}` : 'Service inconnu';
            case 'group':
              const foundGroup = this.formOptions.groups().find((group) => group.id === value.id);
              return foundGroup ? `Groupe - ${foundGroup.description}` : 'Groupe inconnu';
            default:
              return 'Inconnu';
          }
        },
      },
      {
        field: 'message',
        header: 'Message',
      },
    ];

    this.actionsColumns.set(actionsColumns);
    this.recipientsColumns.set(recipientsColumns);
  }

  public refreshRecipients(): void {
    this.fetchActionsAndRecipients();
    this.isAddActionFormDialogOpen.set(false);
  }

  private addRecipients(selectedRecipients: {
    contactArray: { id: number }[];
    usersArray: { typeContactID: number; userId: number }[];
    freeRecipientArray: string[];
    groupsArray: { id: number }[];
    serviceArray: { id: number }[];
    message: string | null;
  }): void {
    const {
      contactArray: selectedContacts,
      usersArray: selectedUsers,
      freeRecipientArray: selectedFreeRecipients,
      groupsArray: selectedGroups,
      serviceArray: selectedServices,
      message,
    } = selectedRecipients;

    const dto: AddRecipientsToPoint = {
      contacts: [
        ...selectedUsers.map((contact) => ({
          contactIdiAdmin: contact.typeContactID,
          contactUserId: contact.userId,
        })),
        ...selectedContacts.map((contact) => ({
          contactId: contact.id,
        })),
      ],
      groupeIds: selectedGroups.map((group) => group.id),
      serviceIds: selectedServices.map((service) => service.id),
      libre: selectedFreeRecipients.join(','),
      message: message ?? undefined,
    };

    let req: Observable<unknown>;

    if (this.target().scope === 'point') {
      req = this.pointsService.addRecipientsToPoint(this.target().id, dto);
    } else {
      req = this.meetingsService.addRecipientsToMeeting(this.target().id, dto);
    }

    req.subscribe({
      next: () => {
        this.notifier.showSuccess(new NotificationSuccess({ message: 'Destinataires ajoutés !' }));
        this.fetchActionsAndRecipients();
      },
    });
  }

  public openAddRecipientsFormDialog(): void {
    const config = {
      showHeader: true,
      header: 'Ajouter des destinataires',
      width: '70%',
      styleClass: 'super-compos-modal-content',
      data: {
        actualRecipients: this.recipients(),
        destinataires: this.recipients(),
      },
      closable: false,
    };

    this.dialogService
      .open(AddRecipientsComponent, config)
      .onClose.subscribe(
        (recipients: {
          contactArray: any[];
          usersArray: any[];
          groupsArray: any[];
          serviceArray: any[];
          freeRecipientArray: any[];
          message: string | null;
        }) => {
          if (!recipients) return;
          this.addRecipients(recipients);
        }
      );
  }

  public onActionProcessClick(action: PointAction | MeetingAction): void {
    this.actionToProcess.set(action);
    this.isActionProcessDialogOpen.set(true);
  }

  public onActionProcessed(): void {
    this.actionToProcess.set(null);
    this.isActionProcessDialogOpen.set(false);
    this.fetchActionsAndRecipients();
  }

  public onRecipientsSelectionChange(items: { kind: 'user' | 'group' | 'service'; main: boolean }[]): void {
    const canSetRecipientAsMain = items.length === 1 && items[0].kind === 'user' && !items[0].main;

    if (!canSetRecipientAsMain) {
      this.addPossibilityToSetRecipientAsMain();
    } else {
      this.removePossibilityToSetRecipientAsMain();
    }
  }

  private addPossibilityToSetRecipientAsMain(): void {
    this.recipientsTableActions.set([
      {
        label: 'Définir comme principal',
        icon: 'bi bi-star-fill',
        command: (event) => {
          console.log(event);
        },
      },
    ]);
  }

  private removePossibilityToSetRecipientAsMain(): void {
    this.recipientsTableActions.set([]);
  }

  private setRecipientAsMain(recipientId: number): void {
    if (this.target().scope === 'point') {
      this.pointsService.setPointRecipientAsDefault(this.target().id, recipientId).subscribe({
        next: () => {
          this.notifier.showSuccess(
            new NotificationSuccess({ message: 'Le destinataire a été défini comme principal' })
          );
          this.refreshRecipients();
        },
        error: () => {
          this.notifier.showError(
            new NotificationError({
              message: 'Une erreur est survenue lors de la définition du destinataire comme principal.',
            })
          );
        },
      });
    } else {
      this.meetingsService.setMeetingRecipientAsDefault(this.target().id, recipientId).subscribe({
        next: () => {
          this.notifier.showSuccess(
            new NotificationSuccess({ message: 'Le destinataire a été défini comme principal' })
          );
          this.refreshRecipients();
        },
        error: () => {
          this.notifier.showError(
            new NotificationError({
              message: 'Une erreur est survenue lors de la définition du destinataire comme principal.',
            })
          );
        },
      });
    }
  }

  public onDeleteRecipients(recipients: ITableItem[]): void {
    if (!recipients.length) return;

    if (this.target().scope === 'point') {
      this.pointsService
        .deletePointRecipients(
          this.target().id,
          (recipients as { detailId: number }[]).map((r) => r.detailId)
        )
        .subscribe({
          next: () => {
            this.notifier.showSuccess(new NotificationSuccess({ message: 'Destinataire(s) supprimé(s) !' }));
            this.fetchActionsAndRecipients();
          },
          error: () => {
            this.notifier.showError(
              new NotificationError({ message: 'Une erreur est survenue lors de la suppression des destinataires.' })
            );
          },
        });
    } else {
      this.meetingsService
        .deleteMeetingRecipients(
          this.target().id,
          (recipients as { detailId: number }[]).map((r) => r.detailId)
        )
        .subscribe({
          next: () => {
            this.notifier.showSuccess(new NotificationSuccess({ message: 'Destinataire(s) supprimé(s) !' }));
            this.fetchActionsAndRecipients();
          },
          error: () => {
            this.notifier.showError(
              new NotificationError({ message: 'Une erreur est survenue lors de la suppression des destinataires.' })
            );
          },
        });
    }
  }
}
